import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Pencil, Trash2, Search, X, Loader2, Send, Save, Eye, Sparkles, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, AreaF, SelectF, CheckF, ImageF, StatusBadge, inputCls } from "./AFields";
import { RichTextF, toHtml } from "./RichText";
import { VersionHistory } from "./Versions";
import AICreate from "./AICreate";
import AIImageStudio from "./AIImageStudio";

const ENTITY_COLL = { "work-areas": "work_areas", projects: "projects", articles: "articles", news: "news_events", gallery: "gallery" };

const CONFIGS = {
  "work-areas": {
    title: "Our Work Areas", ai: true,
    fields: [
      { k: "title_en", l: "Title (English)", t: "text", req: true }, { k: "title_hi", l: "Title (Hindi)", t: "text" },
      { k: "short_desc_en", l: "Short Description (English)", t: "area", rows: 2, ai: true }, { k: "short_desc_hi", l: "Short Description (Hindi)", t: "area", rows: 2, ai: true },
      { k: "desc_en", l: "Full Description (English)", t: "rich", ai: true }, { k: "desc_hi", l: "Full Description (Hindi)", t: "rich", ai: true },
      { k: "image", l: "Image", t: "image" }, { k: "image_prompt", l: "AI Image Prompt", t: "area", rows: 2 }, { k: "alt_text", l: "Image Alt Text", t: "text" },
      { k: "icon", l: "Icon", t: "select", opts: ["BookOpen", "HeartPulse", "ShieldCheck", "Users", "Home", "HandHeart", "Wrench", "Megaphone", "Baby", "LifeBuoy"] },
      { k: "stats", l: "Statistics (e.g. 500+ beneficiaries)", t: "text" },
      { k: "order", l: "Order", t: "number" }, { k: "published", l: "Published", t: "check" },
      { k: "seo_title", l: "SEO Title", t: "text" }, { k: "seo_desc", l: "SEO Description", t: "text" },
    ],
    columns: [["title_en", "Title"], ["stats", "Stats"], ["order", "Order"]],
  },
  projects: {
    title: "Projects", ai: true,
    fields: [
      { k: "title_en", l: "Title (English)", t: "text", req: true }, { k: "title_hi", l: "Title (Hindi)", t: "text" },
      { k: "slug", l: "Slug (optional)", t: "text" }, { k: "category", l: "Category", t: "text" }, { k: "location", l: "Location", t: "text" },
      { k: "short_desc_en", l: "Short Description (English)", t: "area", rows: 2, ai: true }, { k: "short_desc_hi", l: "Short Description (Hindi)", t: "area", rows: 2, ai: true },
      { k: "description_en", l: "Detailed Description (English)", t: "rich", ai: true }, { k: "description_hi", l: "Detailed Description (Hindi)", t: "rich", ai: true },
      { k: "objectives_en", l: "Objectives (English)", t: "rich", ai: true }, { k: "objectives_hi", l: "Objectives (Hindi)", t: "rich", ai: true },
      { k: "activities_en", l: "Activities (English)", t: "rich", ai: true }, { k: "activities_hi", l: "Activities (Hindi)", t: "rich", ai: true },
      { k: "start_date", l: "Start Date", t: "date" }, { k: "end_date", l: "End Date (optional)", t: "date" },
      { k: "status", l: "Status", t: "select", opts: ["draft", "published", "archived"] },
      { k: "image", l: "Featured Image", t: "image" }, { k: "image_prompt", l: "AI Image Prompt", t: "area", rows: 2 }, { k: "alt_text", l: "Image Alt Text", t: "text" },
      { k: "beneficiaries", l: "Beneficiary Information", t: "area", rows: 2 },
      { k: "impact", l: "Impact", t: "area", rows: 2 }, { k: "outcome", l: "Outcome", t: "area", rows: 2 },
      { k: "seo_title", l: "SEO Title", t: "text" }, { k: "seo_desc", l: "SEO Description", t: "text" },
      { k: "seo_keywords", l: "SEO Keywords", t: "text" }, { k: "social_desc", l: "Social Description", t: "area", rows: 2 },
      { k: "show_on_home", l: "Feature on Homepage", t: "check" }, { k: "feature_order", l: "Feature Priority", t: "number" }, { k: "feature_until", l: "Featured Until (optional)", t: "date" },
    ],
    columns: [["title_en", "Title"], ["category", "Category"], ["location", "Location"]],
  },
  articles: {
    title: "Articles", ai: true,
    fields: [
      { k: "title", l: "Title", t: "text", req: true }, { k: "title_hi", l: "Title (Hindi)", t: "text" },
      { k: "subtitle", l: "Subtitle", t: "text" }, { k: "slug", l: "Slug (optional)", t: "text" },
      { k: "category", l: "Category", t: "text" }, { k: "author", l: "Author", t: "text" }, { k: "date", l: "Date", t: "date" },
      { k: "image", l: "Featured Image", t: "image" }, { k: "image_prompt", l: "AI Image Prompt", t: "area", rows: 2 }, { k: "alt_text", l: "Image Alt Text", t: "text" },
      { k: "excerpt", l: "Excerpt", t: "area", rows: 2, ai: true },
      { k: "body", l: "Article Body", t: "rich", ai: true }, { k: "body_hi", l: "Article Body (Hindi)", t: "rich", ai: true },
      { k: "tags_csv", l: "Tags (comma separated)", t: "text" },
      { k: "status", l: "Status", t: "select", opts: ["draft", "published", "scheduled", "archived"] },
      { k: "publish_at", l: "Publish At (for scheduled)", t: "datetime-local" },
      { k: "seo_title", l: "SEO Title", t: "text" }, { k: "seo_desc", l: "SEO Description", t: "text" },
      { k: "seo_keywords", l: "SEO Keywords", t: "text" }, { k: "social_desc", l: "Social Description", t: "area", rows: 2 },
      { k: "show_on_home", l: "Feature on Homepage", t: "check" }, { k: "feature_order", l: "Feature Priority", t: "number" }, { k: "feature_until", l: "Featured Until (optional)", t: "date" },
    ],
    columns: [["title", "Title"], ["category", "Category"], ["author", "Author"], ["date", "Date"]],
  },
  news: {
    title: "News & Events", ai: true,
    fields: [
      { k: "title", l: "Title", t: "text", req: true }, { k: "title_hi", l: "Title (Hindi)", t: "text" }, { k: "slug", l: "Slug (optional)", t: "text" },
      { k: "type", l: "Type", t: "select", opts: ["news", "event"] },
      { k: "date", l: "Event/News Date", t: "date" },
      { k: "start_time", l: "Start Time (events)", t: "text" }, { k: "end_time", l: "End Time (events)", t: "text" },
      { k: "venue", l: "Venue", t: "text" }, { k: "address", l: "Address / Map Link", t: "text" },
      { k: "description", l: "Description", t: "rich", ai: true }, { k: "description_hi", l: "Description (Hindi)", t: "rich", ai: true },
      { k: "image", l: "Featured Image", t: "image" }, { k: "image_prompt", l: "AI Image Prompt", t: "area", rows: 2 }, { k: "alt_text", l: "Image Alt Text", t: "text" },
      { k: "reg_link", l: "Registration/Contact Link", t: "text" }, { k: "related_project", l: "Related Project (slug, optional)", t: "text" },
      { k: "event_status", l: "Event Status (past-dated events auto-move to Past Events — never deleted)", t: "select", opts: ["upcoming", "ongoing", "completed", "past"] },
      { k: "status", l: "Publish Status", t: "select", opts: ["draft", "published", "archived"] },
      { k: "seo_title", l: "SEO Title", t: "text" }, { k: "seo_desc", l: "SEO Description", t: "text" },
      { k: "seo_keywords", l: "SEO Keywords", t: "text" }, { k: "social_desc", l: "Social Description", t: "area", rows: 2 },
      { k: "show_on_home", l: "Feature on Homepage", t: "check" }, { k: "feature_order", l: "Feature Priority", t: "number" }, { k: "feature_until", l: "Featured Until (optional)", t: "date" },
    ],
    columns: [["title", "Title"], ["type", "Type"], ["date", "Date"]],
  },
  gallery: {
    title: "Gallery", ai: true,
    fields: [
      { k: "image", l: "Image", t: "image", req: true }, { k: "image_prompt", l: "AI Image Prompt", t: "area", rows: 2 },
      { k: "heading", l: "Heading", t: "text" }, { k: "caption", l: "Caption", t: "text" }, { k: "alt_text", l: "Alt Text", t: "text" },
      { k: "category", l: "Category", t: "text" }, { k: "album_id", l: "Album", t: "album" },
      { k: "event", l: "Event/Project association", t: "text" }, { k: "date", l: "Date", t: "date" },
      { k: "order", l: "Order", t: "number" }, { k: "published", l: "Published", t: "check" }, { k: "show_on_home", l: "Feature on Homepage", t: "check" },
      { k: "feature_order", l: "Feature Priority", t: "number" }, { k: "feature_until", l: "Featured Until (optional)", t: "date" },
      { k: "seo_title", l: "SEO Title", t: "text" }, { k: "seo_desc", l: "SEO Description", t: "text" },
      { k: "seo_keywords", l: "SEO Keywords", t: "text" }, { k: "social_desc", l: "Social Description", t: "area", rows: 2 },
    ],
    columns: [["heading", "Heading"], ["category", "Category"], ["date", "Date"]],
  },
};

function CollectionPreview({ editing, entity }) {
  const title = editing.title || editing.title_en || editing.heading || "Untitled";
  const body = editing.body || editing.description || editing.description_en || editing.desc_en || editing.short_desc_en || editing.excerpt || "";
  const image = editing.image || editing.cover_image;
  const meta = [editing.category, editing.author, editing.date, editing.location].filter(Boolean).join(" · ");
  return (
    <div className="mt-6 rounded-lg border border-navy/15 bg-navy-50/50 p-5" data-testid="collection-preview">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Draft preview — {entity}</p>
      {image && <img src={image} alt={editing.alt_text || ""} className="mt-4 max-h-64 w-full rounded-lg object-cover" />}
      <h2 className="mt-4 font-display text-2xl font-bold text-navy">{title}</h2>
      {editing.subtitle && <p className="mt-1 text-sm font-semibold text-slate-500">{editing.subtitle}</p>}
      {meta && <p className="mt-2 text-xs font-bold uppercase tracking-wider text-saffron-600">{meta}</p>}
      <div className="prose-ssf mt-4 max-w-none text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: toHtml(body) }} />
    </div>
  );
}

export default function Collections() {
  const { entity } = useParams();
  const cfg = CONFIGS[entity];
  const [data, setData] = useState({ items: [], total: 0 });
  const [albums, setAlbums] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState("");
  const [preview, setPreview] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [imageAIOpen, setImageAIOpen] = useState(false);
  const editingRef = useRef(null);
  const dirtyRef = useRef(false);

  const load = () => {
    if (!cfg) return;
    api.get(`/admin/collections/${entity}?q=${encodeURIComponent(q)}&status=${status}&limit=100`)
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, [entity, status]); // eslint-disable-line
  useEffect(() => {
    if (entity === "gallery") api.get("/admin/albums").then((r) => setAlbums(r.data)).catch(() => {});
  }, [entity]);

  useEffect(() => { editingRef.current = editing; }, [editing]);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  const hasStatus = useMemo(() => cfg?.fields.some((f) => f.k === "status"), [cfg]);
  const hasWorkflow = useMemo(() => cfg?.fields.some((f) => f.k === "status" || f.k === "published"), [cfg]);

  useEffect(() => {
    const h = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  useEffect(() => {
    if (!editing || !hasWorkflow) return undefined;
    const iv = setInterval(async () => {
      const cur = editingRef.current;
      if (!dirtyRef.current || !cur?.id) return;
      setSaveState("saving");
      try {
        const body = prepare({ ...cur });
        delete body.has_draft;
        await api.put(`/admin/collections/${entity}/${cur.id}`, { ...body, _as_draft: true });
        setDirty(false);
        setSaveState("saved");
      } catch { setSaveState(""); }
    }, 20000);
    return () => clearInterval(iv);
  }, [editing !== null, entity]); // eslint-disable-line

  if (!cfg) return <p className="text-sm text-slate-500">Unknown collection.</p>;

  function prepare(body) {
    if (body.tags_csv !== undefined) {
      body.tags = String(body.tags_csv).split(",").map((s) => s.trim()).filter(Boolean);
      delete body.tags_csv;
    }
    if (entity === "articles" && body.status === "scheduled" && body.publish_at) {
      body.publish_at = new Date(body.publish_at).toISOString();
    }
    return body;
  }

  const makeBlank = () => {
    const blank = {};
    cfg.fields.forEach((f) => {
      blank[f.k] = f.t === "check" ? (f.k === "published" ? true : false)
        : f.k === "status" ? "draft"
        : f.k === "event_status" ? "upcoming"
        : f.k === "type" ? "news" : "";
    });
    return blank;
  };

  const openNew = () => {
    setEditing(makeBlank()); setDirty(false); setSaveState(""); setPreview(false);
  };

  const applyAICreate = (fields) => {
    const generated = { ...makeBlank(), ...fields };
    if (hasStatus) generated.status = "draft";
    if ("published" in generated) generated.published = false;
    generated.show_on_home = false;
    setEditing(generated);
    setDirty(true); setSaveState(""); setPreview(false);
    toast.success("AI content opened in editor — review, save draft, then publish");
  };

  const applyAIImage = (media) => {
    const blank = makeBlank();
    setEditing({
      ...blank,
      image: media.url, thumb: media.thumb, heading: media.heading || media.title || "",
      caption: media.caption || "", alt_text: media.alt_text || "", category: media.category || "",
      seo_title: media.seo_title || "", seo_desc: media.seo_desc || "",
      tags_csv: Array.isArray(media.tags) ? media.tags.join(", ") : "",
      published: false, show_on_home: false,
    });
    setDirty(true); setSaveState(""); setPreview(false);
  };

  const suggestGalleryMeta = async () => {
    try {
      const prompt = editing.image_prompt || editing.heading || editing.caption || "Sahaj Sarathi Foundation gallery image";
      const r = await api.post("/ai/cms/image-metadata", { prompt, entity: "gallery" });
      const m = r.data;
      setEditing({
        ...editing,
        heading: editing.heading || m.heading || m.title || "",
        caption: editing.caption || m.caption || "",
        alt_text: editing.alt_text || m.alt_text || "",
        category: editing.category || m.category || "",
        seo_title: editing.seo_title || m.seo_title || "",
        seo_desc: editing.seo_desc || m.seo_desc || "",
      });
      setDirty(true);
      toast.success("AI caption and metadata inserted — review before saving");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openEdit = async (item) => {
    try {
      const r = await api.get(`/admin/collections/${entity}/${item.id}`);
      const doc = r.data;
      setEditing({ ...doc, tags_csv: Array.isArray(doc.tags) ? doc.tags.join(", ") : doc.tags_csv || "" });
      setDirty(false); setSaveState(doc.has_draft ? "saved" : ""); setPreview(false);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async (mode = "live") => {
    setBusy(true);
    try {
      const body = prepare({ ...editing });
      delete body.has_draft;
      const isNew = !body.id;
      if (isNew && hasWorkflow) {
        if (hasStatus) body.status = "draft";
        else body.published = false;
      }
      let id = body.id;
      if (isNew) {
        const r = await api.post(`/admin/collections/${entity}`, body);
        id = r.data.id;
      } else if (hasWorkflow && (mode === "draft" || mode === "publish")) {
        await api.put(`/admin/collections/${entity}/${id}`, { ...body, _as_draft: true });
      } else {
        await api.put(`/admin/collections/${entity}/${id}`, body);
      }
      if (hasWorkflow && mode === "publish") {
        await api.post(`/admin/collections/${entity}/${id}/publish`);
        toast.success("Published — live on the website now");
      } else {
        toast.success(hasWorkflow && mode === "draft" ? "Draft saved — the live website is unchanged" : "Saved");
      }
      setDirty(false); setEditing(null); setPreview(false); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${item.title || item.title_en || item.heading}"? Use Cancel to keep it. Permanent deletion is available only from Trash.`)) return;
    try { await api.delete(`/admin/collections/${entity}/${item.id}`); toast.success("Moved to Trash"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const onDropFiles = async (files) => {
    let uploaded = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/admin/upload", fd);
        await api.post(`/admin/collections/${entity}`, { image: r.data.url, thumb: r.data.thumb, heading: file.name.replace(/\.[^.]+$/, ""), published: false, order: 0 });
        uploaded += 1;
      } catch (e) { toast.error(errMsg(e)); }
    }
    if (uploaded) toast.success(`${uploaded} image(s) uploaded as drafts`);
    load();
  };

  return (
    <div data-testid={`admin-${entity}`}>
      <SEO title={cfg.title} />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">{cfg.title}</h1>
          <p className="mt-1 text-sm text-slate-500">{data.total} item(s){hasWorkflow ? " · Draft → Preview → Publish: drafts never change the live site" : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {cfg.ai && <button onClick={() => setAiOpen(true)} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid={`${entity}-ai-create-btn`}><Sparkles size={16} /> Create with AI</button>}
          {entity === "gallery" && <button onClick={() => setImageAIOpen(true)} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="gallery-ai-image-btn"><ImagePlus size={16} /> Create Image with AI</button>}
          <button onClick={openNew} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid={`${entity}-new-btn`}><Plus size={16} /> New</button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-3 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search…" className={`${inputCls} !w-64 !pl-10`} data-testid={`${entity}-search`} />
        </div>
        <button onClick={load} className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy" data-testid={`${entity}-search-btn`}>Search</button>
        {hasStatus && (
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} !w-44`} data-testid={`${entity}-status-filter`}>
            <option value="">All statuses</option>
            {["draft", "published", "scheduled", "archived"].map((s) => <option key={s}>{s}</option>)}
          </select>
        )}
      </div>

      {entity === "gallery" && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); onDropFiles([...e.dataTransfer.files]); }}
          className={`mt-5 rounded-lg border-2 border-dashed p-8 text-center text-sm font-semibold transition-colors ${dragging ? "border-saffron-500 bg-saffron-100/50 text-navy" : "border-gray-300 bg-white text-slate-500"}`}
          data-testid="gallery-dropzone">
          Drag & drop images here to upload multiple at once (uploaded as drafts, auto-optimised, thumbnails generated)
        </div>
      )}

      <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-gray-200 bg-navy-50/50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              {entity === "gallery" && <th className="px-4 py-3">Image</th>}
              {cfg.columns.map(([k, l]) => <th key={k} className="px-4 py-3">{l}</th>)}
              {hasStatus && <th className="px-4 py-3">Status</th>}
              {!hasStatus && hasWorkflow && <th className="px-4 py-3">Published</th>}
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <tr key={item.id} className="border-b border-gray-100 last:border-0 hover:bg-navy-50/30" data-testid={`${entity}-row-${item.id}`}>
                {entity === "gallery" && <td className="px-4 py-3">{item.thumb || item.image ? <img src={item.thumb || item.image} alt={item.alt_text || ""} className="h-12 w-12 rounded-md object-cover" loading="lazy" /> : "—"}</td>}
                {cfg.columns.map(([k]) => <td key={k} className="max-w-[220px] truncate px-4 py-3 font-medium text-navy">{String(item[k] ?? "").replace(/<[^>]+>/g, "").slice(0, 80) || "—"}</td>)}
                {hasStatus && (
                  <td className="px-4 py-3">
                    <StatusBadge value={item.status} />
                    {item.draft && <span className="ml-1.5 rounded-full bg-saffron-100 px-2 py-0.5 text-[10px] font-bold text-saffron-600" data-testid={`${entity}-draft-badge-${item.id}`}>DRAFT CHANGES</span>}
                  </td>
                )}
                {!hasStatus && hasWorkflow && (
                  <td className="px-4 py-3">
                    <StatusBadge value={item.published ? "published" : "draft"} />
                    {item.draft && <span className="ml-1.5 rounded-full bg-saffron-100 px-2 py-0.5 text-[10px] font-bold text-saffron-600" data-testid={`${entity}-draft-badge-${item.id}`}>DRAFT CHANGES</span>}
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    <VersionHistory coll={ENTITY_COLL[entity]} itemId={item.id} onRestored={load} hasDraft={!!item.draft} />
                    <button onClick={() => openEdit(item)}
                      className="rounded-md border border-gray-200 p-2 text-navy transition-colors hover:border-saffron-500" data-testid={`${entity}-edit-${item.id}`} aria-label="Edit"><Pencil size={14} /></button>
                    <button onClick={() => remove(item)} className="rounded-md border border-gray-200 p-2 text-red-500 transition-colors hover:border-red-400" data-testid={`${entity}-delete-${item.id}`} aria-label="Delete"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">No items yet. Click "New" or "Create with AI".</td></tr>}
          </tbody>
        </table>
      </div>

      <AICreate entity={entity} title={cfg.title} open={aiOpen} onClose={() => setAiOpen(false)} onApply={applyAICreate} />
      {entity === "gallery" && <AIImageStudio open={imageAIOpen} onClose={() => setImageAIOpen(false)} onUse={applyAIImage} entity="gallery" saveLabel="Use Image" />}

      {editing && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-navy/60 p-4 backdrop-blur-sm" data-testid={`${entity}-editor`}>
          <div className="my-8 w-full max-w-3xl rounded-xl bg-white p-6 shadow-2xl sm:p-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-xl font-bold text-navy">{editing.id ? "Edit" : "New"} — {cfg.title}</h2>
                {hasWorkflow && (
                  <p className="mt-1 text-xs font-bold" data-testid="editor-save-state">
                    {saveState === "saving" ? <span className="text-saffron-600">Saving draft…</span>
                      : saveState === "saved" ? <span className="text-brandgreen">Draft saved</span>
                      : dirty ? <span className="text-slate-500">Unsaved changes</span>
                      : editing.has_draft ? <span className="text-saffron-600">Editing unpublished draft</span>
                      : <span className="text-slate-400">No changes</span>}
                  </p>
                )}
              </div>
              <button onClick={() => { if (!dirty || window.confirm("Discard unsaved changes?")) { setEditing(null); setDirty(false); setPreview(false); } }} className="rounded-full p-1.5 text-slate-400 hover:bg-gray-100" data-testid="editor-close" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              {cfg.fields.map((f) => {
                const val = editing[f.k];
                const setF = (v) => { setEditing({ ...editing, [f.k]: v }); setDirty(true); setSaveState(""); };
                const wrap = f.t === "area" || f.t === "rich" || f.t === "image" || f.t === "check" ? "sm:col-span-2" : "";
                return (
                  <div key={f.k} className={wrap}>
                    {f.t === "text" && <TextF label={f.l} value={val} onChange={setF} required={f.req} />}
                    {f.t === "number" && <TextF label={f.l} type="number" value={val} onChange={(v) => setF(Number(v))} />}
                    {f.t === "date" && <TextF label={f.l} type="date" value={val} onChange={setF} />}
                    {f.t === "datetime-local" && <TextF label={f.l} type="datetime-local" value={val ? String(val).slice(0, 16) : ""} onChange={setF} />}
                    {f.t === "select" && <SelectF label={f.l} value={val} onChange={setF} options={f.opts} />}
                    {f.t === "area" && <AreaF label={f.l} value={val} onChange={setF} rows={f.rows || 4} ai={f.ai} contentType={entity} />}
                    {f.t === "rich" && <RichTextF label={f.l} value={val} onChange={setF} ai={f.ai} contentType={entity} />}
                    {f.t === "check" && <CheckF label={f.l} value={val} onChange={setF} />}
                    {f.t === "image" && <ImageF label={f.l} value={val} onChange={setF} />}
                    {f.t === "album" && (
                      <div>
                        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{f.l}</label>
                        <select value={val ?? ""} onChange={(e) => setF(e.target.value)} className={inputCls} data-testid="f-album">
                          <option value="">No album</option>
                          {albums.map((a) => <option key={a.id} value={a.id}>{a.title_en}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {entity === "gallery" && (
              <div className="mt-5 rounded-lg border border-gray-200 bg-navy-50/50 p-4">
                <button type="button" onClick={suggestGalleryMeta} className="btn-dark !rounded-md !px-4 !py-2 text-xs" data-testid="gallery-ai-caption">
                  <Sparkles size={14} /> AI Caption
                </button>
                <p className="mt-2 text-xs text-slate-500">Suggests heading, caption, alt text, category and SEO metadata without publishing.</p>
              </div>
            )}
            {preview && <CollectionPreview editing={editing} entity={entity} />}
            <div className="mt-8 flex flex-wrap justify-end gap-3">
              <button onClick={() => { if (!dirty || window.confirm("Discard unsaved changes?")) { setEditing(null); setDirty(false); setPreview(false); } }} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
              <button onClick={() => setPreview(!preview)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy" data-testid="editor-preview">
                <Eye size={15} /> {preview ? "Hide Preview" : "Preview"}
              </button>
              {hasWorkflow ? (
                <>
                  <button onClick={() => save("draft")} disabled={busy} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="editor-save-draft">
                    <Save size={15} /> Save Draft
                  </button>
                  <button onClick={() => save("publish")} disabled={busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="editor-publish">
                    {busy && <Loader2 size={15} className="animate-spin" />} <Send size={14} /> Publish
                  </button>
                </>
              ) : (
                <button onClick={() => save("live")} disabled={busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="editor-save">
                  {busy && <Loader2 size={15} className="animate-spin" />} Save
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
